import { OneOrManySchema, isDefined } from 'notable-common';
import { z } from 'zod';
import { FlowRunState } from './flowRunState.js';
import { FlowRunType } from './runType.js';
import { FlowApiPageLimitSchema, FlowApiPageOffsetSchema } from '../pagination/pageLimit.js';
const FLOW_RUN_EXPLORE_MAX_COLUMNS = 500;
/** How deep into a flow's subflows the column catalog reaches. */
export var FlowRunExploreColumnScope;
(function (FlowRunExploreColumnScope) {
    FlowRunExploreColumnScope["TopLevel"] = "TopLevel";
    FlowRunExploreColumnScope["IncludeSubflows"] = "IncludeSubflows";
})(FlowRunExploreColumnScope || (FlowRunExploreColumnScope = {}));
/** Whether a column reads a property of the run itself or the output of one of its blocks. */
export const FlowRunExploreColumnTypeSchema = z.enum(['FlowRun', 'BlockOutput']);
export const FlowRunExploreColumnType = FlowRunExploreColumnTypeSchema.enum;
export const FLOW_RUN_EXPLORE_COLUMN_KEY_DELIMITER = ':';
export const FLOW_RUN_EXPLORE_BLOCK_OUTPUT_COLUMN_KEY_PREFIX = `${FlowRunExploreColumnType.BlockOutput}${FLOW_RUN_EXPLORE_COLUMN_KEY_DELIMITER}`;
const FlowRunExploreColumnKeyPatternSchema = z.templateLiteral([
    FlowRunExploreColumnTypeSchema,
    FLOW_RUN_EXPLORE_COLUMN_KEY_DELIMITER,
    z.string().min(1),
]);
// Keep the inferred column contract compatible with existing string-based clients while validating
// the template-literal format at runtime.
export const FlowRunExploreColumnKeySchema = FlowRunExploreColumnKeyPatternSchema;
export function toFlowRunExploreColumnKey(args) {
    const { type, property } = args;
    return FlowRunExploreColumnKeyPatternSchema.parse(`${type}${FLOW_RUN_EXPLORE_COLUMN_KEY_DELIMITER}${property}`);
}
export function toFlowRunColumnKey(property) {
    return toFlowRunExploreColumnKey({ type: FlowRunExploreColumnType.FlowRun, property });
}
export function toBlockOutputColumnKey(property) {
    return toFlowRunExploreColumnKey({ type: FlowRunExploreColumnType.BlockOutput, property });
}
export function isBlockOutputColumnKey(columnKey) {
    return (columnKey.startsWith(FLOW_RUN_EXPLORE_BLOCK_OUTPUT_COLUMN_KEY_PREFIX) &&
        FlowRunExploreColumnKeySchema.safeParse(columnKey).success);
}
/** Query parameters `GET /flow-runs/explore/columns` accepts. */
export const FlowRunExploreColumnsQuerySchema = z.object({
    flowId: z.uuid().describe('List the columns available for this flow.'),
    columnScope: z
        .enum(FlowRunExploreColumnScope)
        .describe('Whether to include the columns of nested subflows.')
        .optional(),
});
const FlowRunExploreColumnBaseSchema = z.object({
    key: FlowRunExploreColumnKeySchema,
    displayName: z.string(),
    type: FlowRunExploreColumnTypeSchema,
});
function columnKeyMatchesType(column) {
    return column.key.startsWith(`${column.type}${FLOW_RUN_EXPLORE_COLUMN_KEY_DELIMITER}`);
}
export const FlowRunExploreColumnSchema = FlowRunExploreColumnBaseSchema.refine(columnKeyMatchesType, { path: ['key'], message: 'Column key must match the column type.' });
/** A column as requested for an export, where the server supplies any display name left out. */
export const FlowRunExploreRequestColumnSchema = FlowRunExploreColumnBaseSchema.partial({
    displayName: true,
}).refine(columnKeyMatchesType, {
    path: ['key'],
    message: 'Column key must match the column type.',
});
const FlowRunExploreBlockOutputColumnKeySchema = FlowRunExploreColumnKeySchema.refine((columnKey) => columnKey.startsWith(FLOW_RUN_EXPLORE_BLOCK_OUTPUT_COLUMN_KEY_PREFIX), 'Expected a block-output column key.');
/** A point in time a caller filters runs by, represented as an ISO-8601 string. */
const FlowRunExploreDateFilterSchema = z.iso.datetime({ offset: true });
/**
 * Query parameters `GET /flow-runs/explore` accepts.
 *
 * flow-api layers on a check that every date falls within the run retention window, which depends
 * on that service's configuration and so cannot live here.
 */
export const FlowRunExploreQuerySchema = z.object({
    flowId: z.uuid().describe('Explore runs of this flow.'),
    limit: FlowApiPageLimitSchema.describe('How many runs to return.').optional(),
    offset: FlowApiPageOffsetSchema.describe('How many matches to skip, for paging.').optional(),
    createdBefore: FlowRunExploreDateFilterSchema.optional(),
    createdAfter: FlowRunExploreDateFilterSchema.optional(),
    startedBefore: FlowRunExploreDateFilterSchema.optional(),
    startedAfter: FlowRunExploreDateFilterSchema.optional(),
    endedBefore: FlowRunExploreDateFilterSchema.optional(),
    endedAfter: FlowRunExploreDateFilterSchema.optional(),
    orderBy: z.string().describe('Column to order by, such as created.').optional(),
    orderDirection: z.enum(['asc', 'desc']).describe('Direction to order by.').optional(),
    state: OneOrManySchema(z.enum(FlowRunState))
        .describe('Return only runs in this state, or in any of these states.')
        .optional(),
    type: z.enum(FlowRunType).describe('Return only runs of this type, such as Test.').optional(),
    groupId: z.uuid().describe('Return only runs of this bulk or re-run group.').optional(),
    version: OneOrManySchema(z.string())
        .describe('Return only runs of this flow definition version.')
        .optional(),
    projectBlockOutputColumns: z
        .union([z.boolean(), z.enum(['true', 'false']).transform((value) => value === 'true')])
        .describe('Include a column per block output, rather than run columns alone.')
        .optional(),
    blockOutputColumns: OneOrManySchema(FlowRunExploreBlockOutputColumnKeySchema, {
        arrayMax: FLOW_RUN_EXPLORE_MAX_COLUMNS,
    })
        .describe('Include a column for each of these block outputs.')
        .optional(),
});
/**
 * Query parameters `GET /flow-runs/explore/count` accepts: the same filters without paging,
 * ordering, or column projection.
 */
export const FlowRunExploreCountQuerySchema = FlowRunExploreQuerySchema.omit({
    limit: true,
    offset: true,
    orderBy: true,
    orderDirection: true,
    projectBlockOutputColumns: true,
    blockOutputColumns: true,
});
/**
 * Body the export endpoints accept: the explorer query selecting the runs, minus paging, plus the
 * columns to write out.
 */
export const FlowRunExploreExportBodySchema = FlowRunExploreQuerySchema.omit({
    limit: true,
    offset: true,
}).extend({
    columns: z
        .array(FlowRunExploreRequestColumnSchema)
        .max(FLOW_RUN_EXPLORE_MAX_COLUMNS)
        .describe('Columns to write, in order.'),
});
const FLOW_RUN_EXPLORE_DATE_FIELDS = [
    'createdAfter',
    'createdBefore',
    'startedBefore',
    'startedAfter',
    'endedBefore',
    'endedAfter',
];
/** Builds the explorer request schemas with a service-owned date policy. */
export function createFlowRunExploreRequestSchemas(options) {
    const { getDateFilterError } = options;
    const refineDateFilters = (query, context) => {
        for (const field of FLOW_RUN_EXPLORE_DATE_FIELDS) {
            const value = query[field];
            if (!isDefined(value)) {
                continue;
            }
            const parsedValue = FlowRunExploreDateFilterSchema.safeParse(value);
            // superRefine also runs after field validation fails, so do not send invalid dates to the service policy.
            if (!parsedValue.success) {
                continue;
            }
            const message = getDateFilterError(parsedValue.data);
            if (isDefined(message)) {
                context.addIssue({ code: 'custom', path: [field], message });
            }
        }
    };
    return {
        FlowRunExploreFindAllRequestQuerySchema: FlowRunExploreQuerySchema.superRefine(refineDateFilters),
        FlowRunExploreCountRequestQuerySchema: FlowRunExploreCountQuerySchema.superRefine(refineDateFilters),
        FlowRunExploreExportRequestSchema: FlowRunExploreExportBodySchema.superRefine(refineDateFilters),
    };
}
export const FlowRunExploreColumnsResponseSchema = z.object({
    columns: z.array(FlowRunExploreColumnSchema),
});
export const FlowRunExploreExportResponseSchema = z.object({
    spreadsheetId: z.string().nullish(),
    spreadsheetUrl: z.string().nullish(),
});
export const FlowRunExploreDownloadResponseSchema = z.object({
    downloadUrl: z.string(),
});
//# sourceMappingURL=explore.js.map